import React, { useState, useEffect, Fragment } from "react";

import { Link } from "@jsluna/link";
import { Form, Checkbox } from "@jsluna/form";
import { Modal, ModalHeading } from "@jsluna/modal";
import { GridWrapper, GridItem } from "@jsluna/grid";
import {
  EmailInputField,
  withFormState,
  SubmitButton,
  PasswordInputField,
  trackEvent,
  BLACK_BOX_ELEMENT_ID,
  Notification,
  NotificationStatuses,
  Card,
  reportError,
} from "@jsainsburyplc/id-ui-common";

import {
  getLoginLink,
  getForgottenPasswordLink,
  getNectarRegisterUrl,
} from "utils/urls";
import { checkIsWebAuthnSupported } from "utils/webAuthn";
import { OPT_OUT_LS_KEY, CUSTOMER_ID, DEVICE_ID } from "constants/webAuthn";
import missionConfig from "missionConfig";
import {
  INVALID_EMAIL_MESSAGE,
  EMAIL_IS_REQUIRED_MESSAGE,
  WEBAUTHN_ASSERTION_ERROR,
} from "constants/messages";
import { categoryTypes, actionTypes } from "constants/tracking";
import { USERNAME, PASSWORD, LOGIN_FORM_ID, CHALLENGE } from "constants/login";
import { setCookie } from "hooks/useCookies";
import missionContent from "missionContent";
import { WEB_AUTHN_FLAG, flagEnabled, SSO_FLAG } from "utils/featureFlags";
import {
  webAuthnService,
  ASSERTION_FINISH_ENDPOINT,
} from "services/webAuthn.service";

import RouterLink from "../RouterLink";
import { MultiMissionHeader } from "../MultiMissionHeader";
import PageFormat from "../PageFormat";

const initialValues = {
  [USERNAME]: "",
  [PASSWORD]: "",
};

const trackFormSubmit = () => {
  trackEvent({
    category: categoryTypes.LOGIN,
    action: actionTypes.SUBMIT,
    label: "Log in",
  });
};

const trackHelpModal = () => {
  trackEvent({
    category: categoryTypes.LOGIN,
    action: actionTypes.CLICK,
    label: "Need help?",
  });
};

const trackResetPassword = () => {
  trackEvent({
    category: categoryTypes.LOGIN,
    action: actionTypes.CLICK,
    label: "Reset password",
  });
};

const trackForgotPassword = () => {
  trackEvent({
    category: categoryTypes.LOGIN,
    action: actionTypes.CLICK,
    label: "Forgot your password?",
  });
};

const trackRegister = (label) => {
  trackEvent({
    category: categoryTypes.LOGIN,
    action: actionTypes.CLICK,
    label,
  });
};

const submitForm = () =>
  // Using promise to ensure the form's state does not change until the form
  // has began to submit. This ensures the button's "loading" state persists
  new Promise(() => {
    const form = document.getElementById(LOGIN_FORM_ID);
    // If the user has entered an email, store the value in USERNAME cookie
    // so that we can display it in the "forgotten-password" page.
    // If the user has not entered an email, it means the email
    // was pre-populated for a Nectar user and should not be stored
    const emailInput = document.querySelector("#form-fields #username");
    const emailValue = emailInput.value.trim();
    emailInput.value = emailValue;
    setCookie(USERNAME, emailInput ? emailInput.value : "", { path: "/" });
    trackFormSubmit();
    form.submit();
  });

const onSubmit = async (_, { setSubmitting }) => {
  try {
    await submitForm();
  } catch (error) {
    reportError(`Submit log in form: ${error}`);
    submitForm();
    setSubmitting(false);
  }
};

export const LoginForm = ({
  handleSubmit,
  errorMessage,
  title,
  loginChallenge = "",
  hasResetPasswordLink = true,
  isSubmitting,
  isValid,
  isIovationEnabled,
}) => {
  const { registerLabel, registerLink } = missionContent.registerNotification;

  const [showHelpModal, setShowHelpModal] = useState(false);
  const [isWebAuthnDevice, setIsWebAuthnDevice] = useState(0);
  const [canLogInWithDevice, setCanLogInWithDevice] = useState(false);
  const [isRememberMe, setIsRememberMe] = useState(0);
  const [isRememberMeEnabled, setIsRememberMeEnabled] = useState(false);
  const [
    webAuthNAssertionErrorMessage,
    setWebAuthNAssertionErrorMessage,
  ] = useState(false);
  const REMEMBER_ME_STORAGE_KEY = "rememberMe";

  useEffect(() => {
    const checkWebAuthn = async () => {
      try {
        const { isAuthenticator } = await checkIsWebAuthnSupported();
        const customerId = localStorage.getItem("customerId");
        setIsWebAuthnDevice(Number(isAuthenticator && !customerId));
      } catch (error) {
        trackEvent({
          category: categoryTypes.WEB_AUTHN,
          action: actionTypes.ERROR,
          label: `${categoryTypes.WEB_AUTHN}: ${error}`,
        });
      }
    };

    if (flagEnabled(WEB_AUTHN_FLAG)) {
      const isOptedOut = JSON.parse(localStorage.getItem(OPT_OUT_LS_KEY));
      !isOptedOut && checkWebAuthn();
    }
  }, []);

  useEffect(() => {
    const customerId = localStorage.getItem(CUSTOMER_ID);
    const currentDeviceId = localStorage.getItem(DEVICE_ID);
    // if the device has been removed by the user from Manage Devices page, then the loginWithDevice will be false and the user is shown the usual login form
    // if the current device is not removed from the "Manage Device" page, then it will continue with the normal loginwithdevice flow.
    // In id-account-ui, when the current device is removed the DEVICE_ID from the storage will be removed and it is checked here before enabling it
    if (customerId && currentDeviceId) {
      setCanLogInWithDevice(true);
    }
  }, []);

  useEffect(() => {
    setIsRememberMeEnabled(flagEnabled(SSO_FLAG));
    const checkRememberMe = async () => {
      const rememberMe = Number(localStorage.getItem(REMEMBER_ME_STORAGE_KEY));
      setIsRememberMe(rememberMe);
    };
    checkRememberMe();
  }, []);

  const handleShowHelpModal = () => {
    trackHelpModal();
    setShowHelpModal(true);
  };

  const handleSetRememberMe = (obj) => {
    const checked = obj.target.checked ? 1 : 0;
    localStorage.setItem(REMEMBER_ME_STORAGE_KEY, checked);
    setIsRememberMe(checked);
  };

  const handleDeviceLogIn = async () => {
    try {
      const customerId = localStorage.getItem("customerId");
      const response = await webAuthnService.logInWithDevice(customerId);
      const form = document.getElementById("log-in-with-device-form");
      form.action = ASSERTION_FINISH_ENDPOINT;

      const customerIdFormElement = document.createElement("input");
      customerIdFormElement.type = "hidden";
      customerIdFormElement.name = "customer_id";
      customerIdFormElement.value = customerId;

      const publicKeyCredentialElement = document.createElement("input");
      publicKeyCredentialElement.type = "hidden";
      publicKeyCredentialElement.name = "public_key_credential";
      publicKeyCredentialElement.value = JSON.stringify(response.publicKeyCred);

      const customerAssertionIdElement = document.createElement("input");
      customerAssertionIdElement.type = "hidden";
      customerAssertionIdElement.name = "customer_assertion_id";
      customerAssertionIdElement.value = response.customerAssertionId;

      form.appendChild(customerIdFormElement);
      form.appendChild(publicKeyCredentialElement);
      form.appendChild(customerAssertionIdElement);
      form.submit();
    } catch (error) {
      setWebAuthNAssertionErrorMessage(WEBAUTHN_ASSERTION_ERROR);
    }
  };

  const handleForgetDevice = () => {
    webAuthnService.deleteDevice().then(() => {
      localStorage.removeItem(CUSTOMER_ID);
      localStorage.removeItem(DEVICE_ID);
    });
    setCanLogInWithDevice(false);
  };

  return (
    <Fragment>
      <PageFormat>
        <div className="ln-u-margin-top*2">
          <MultiMissionHeader />
        </div>

        <GridWrapper>
          <GridItem size={{ xs: "1/2" }}>
            <h1 className="ln-u-h3">{title}</h1>
          </GridItem>

          {!errorMessage && missionConfig.showLoginHelp && (
            <GridItem size={{ xs: "1/2" }} style={{ textAlign: "right" }}>
              <RouterLink
                className="ln-c-link ln-c-link--bare ln-c-link--cta ln-c-link--alpha"
                data-testid="show-help-text"
                onClick={handleShowHelpModal}
                link="#"
                style={{ marginTop: "2px" }}
              >
                Need help?
              </RouterLink>
            </GridItem>
          )}
        </GridWrapper>

        {(errorMessage || webAuthNAssertionErrorMessage) && (
          <div className="ln-u-margin-bottom*2">
            <Notification type={NotificationStatuses.ERROR_TYPE}>
              {errorMessage ? errorMessage : webAuthNAssertionErrorMessage}
              {errorMessage && hasResetPasswordLink && (
                <div>
                  <RouterLink
                    link={getForgottenPasswordLink()}
                    className="ln-c-link ln-c-link--bare ln-c-link--cta ln-c-link--alpha ln-u-push-top-sm"
                    onClick={trackResetPassword}
                  >
                    Reset password
                  </RouterLink>
                </div>
              )}
            </Notification>
          </div>
        )}

        <Card>
          {canLogInWithDevice && (
            <>
              <p>
                You can either log in using your device or by providing your
                usual details.
              </p>
              <div className="ln-u-text-align-center ln-u-push-top-sm">
                <Form method="post" id="log-in-with-device-form">
                  <input
                    type="hidden"
                    name={CHALLENGE}
                    value={loginChallenge}
                  />
                  {isIovationEnabled && (
                    <input
                      type="hidden"
                      name={BLACK_BOX_ELEMENT_ID}
                      id={BLACK_BOX_ELEMENT_ID}
                      data-testid={BLACK_BOX_ELEMENT_ID}
                    />
                  )}
                  <button
                    type="button"
                    style={{ maxWidth: "22rem" }}
                    className="ln-c-button ln-c-button--full ln-c-button--filled"
                    onClick={() => handleDeviceLogIn()}
                  >
                    Log in with device
                  </button>
                </Form>
              </div>
              <div className="ln-u-margin-top*3 ln-u-text-align-center">
                <button
                  style={{ maxWidth: "22rem" }}
                  className="ln-c-button ln-c-button--full ln-c-button--outlined"
                  onClick={() => setCanLogInWithDevice(false)}
                >
                  Log in with password
                </button>
              </div>
              <div className="ln-u-margin-top*3 ln-u-text-align-center">
                <button
                  style={{ maxWidth: "22rem" }}
                  className="ln-c-link ln-c-link--bare ln-c-link--cta ln-c-link--alpha dont-ask-again-link"
                  onClick={() => handleForgetDevice()}
                >
                  Forget device
                </button>
              </div>
            </>
          )}

          {!canLogInWithDevice && (
            <Form
              method="post"
              onSubmit={handleSubmit}
              action={getLoginLink()}
              id={LOGIN_FORM_ID}
            >
              <input
                type="hidden"
                name="web_authn_device"
                value={isWebAuthnDevice}
              />
              <input type="hidden" name={CHALLENGE} value={loginChallenge} />
              {isIovationEnabled && (
                <input
                  type="hidden"
                  name={BLACK_BOX_ELEMENT_ID}
                  id={BLACK_BOX_ELEMENT_ID}
                  data-testid={BLACK_BOX_ELEMENT_ID}
                />
              )}
              <div className="ln-u-push-top-sm">
                <div id="form-fields">
                  <EmailInputField
                    className="ln-u-margin-bottom"
                    label="Email address"
                    name={USERNAME}
                    requiredValidationMessage={EMAIL_IS_REQUIRED_MESSAGE}
                    validationMessage={INVALID_EMAIL_MESSAGE}
                    autoComplete="username"
                    type="email"
                  />
                  <PasswordInputField
                    className="ln-u-margin-bottom"
                    label="Password"
                    name={PASSWORD}
                    validationMessage="Password is required"
                    autoComplete="current-password"
                  />
                </div>
                <RouterLink
                  link={getForgottenPasswordLink()}
                  className="ln-c-link ln-c-link--bare ln-c-link--cta ln-c-link--alpha ln-u-margin-top ln-u-margin-bottom"
                  onClick={trackForgotPassword}
                >
                  Forgot your password?
                </RouterLink>
                {isRememberMeEnabled && (
                  <div id="ln-u-text-align">
                    <Checkbox
                      name="is_remember_me"
                      checked={isRememberMe === 1}
                      value={`${isRememberMe}`}
                      onClick={handleSetRememberMe}
                      label="Stay logged in when using My ID?"
                    />
                  </div>
                )}
              </div>
              <div className="ln-u-text-align-center ln-u-push-top-sm">
                <SubmitButton
                  data-testid="log-in"
                  disabled={!isValid || isSubmitting}
                  submitHandler={handleSubmit}
                  label="Log in"
                  isLoadingLabel="Logging in..."
                  fullWidth={true}
                  isLoading={isSubmitting}
                />
              </div>
            </Form>
          )}
        </Card>

        {!canLogInWithDevice && missionConfig.showRegister && (
          <div className="ln-u-text-align-center ln-u-margin-top*4">
            <div className="ln-u-margin-bottom*2" data-testid="new-to-mission">
              {registerLabel}
            </div>
            <div onClick={() => trackRegister(registerLabel)}>
              {registerLink}
            </div>
          </div>
        )}

        {missionConfig.showLoginHelp && (
          <Modal
            small
            handleClose={() => setShowHelpModal(false)}
            open={showHelpModal}
            headingId="info-modal"
            role="dialog"
            id="info-modal"
            title="Help with logging in"
          >
            <ModalHeading element="h4">Help with logging in</ModalHeading>
            <h5 style={{ marginBottom: "10px" }}>
              Which email address should I use?
            </h5>
            <p>
              You need to use the email associated with your Nectar online
              account. This is the same email address where we send your Nectar
              offers.
            </p>
            <h5 style={{ marginBottom: "10px" }}>
              I’m new to the app and website
            </h5>
            <p>
              You’ll need to digitally{" "}
              <Link
                data-testid="register"
                onClick={() => trackRegister("Need help?")}
                href={getNectarRegisterUrl()}
                className="ln-c-link ln-c-link--bare ln-c-link--alpha"
                style={{ maxWidth: "20rem" }}
              >
                register
              </Link>{" "}
              your card before logging in.
            </p>
            <h5 style={{ marginBottom: "10px" }}>I can’t log in</h5>
            <p>
              You may have an older Nectar account so you’ll need to{" "}
              <Link
                data-testid="register"
                onClick={() => trackRegister("Need help?")}
                href={getNectarRegisterUrl()}
                className="ln-c-link ln-c-link--bare ln-c-link--alpha"
                style={{ maxWidth: "20rem" }}
              >
                register
              </Link>{" "}
              first.
            </p>
          </Modal>
        )}
      </PageFormat>
    </Fragment>
  );
};

export default withFormState({
  mapPropsToValues: (props) => ({
    ...initialValues,
    [USERNAME]: props.email ? props.email : initialValues[USERNAME],
  }),
  handleSubmit: onSubmit,
  validateOnMount: true,
})(LoginForm);
